/**
 * Maps server validation results to client-side validation types
 */

import {
  ServerValidationResultDto,
  ServerValidationIssueDto,
} from '@features/adventures/types/workflow.types'
import {
  WorkflowValidationResult,
  ValidationIssue,
  ValidationSeverity,
  ValidationCategory,
  NodeValidationState,
  GraphAnalysisResult,
} from '@features/adventures/types/validation.types'

/**
 * Maps a server validation issue DTO to the client ValidationIssue format
 */
function mapServerIssueToClient(issue: ServerValidationIssueDto): ValidationIssue {
  // Map severity - server uses strings 'error'/'warning' or numbers 0/1
  let severity: ValidationSeverity
  if (typeof issue.severity === 'number') {
    severity = issue.severity === 0 ? ValidationSeverity.Error : ValidationSeverity.Warning
  } else {
    severity = issue.severity === 'error' ? ValidationSeverity.Error : ValidationSeverity.Warning
  }

  // Map category - server uses strings or numbers
  let category: ValidationCategory
  if (typeof issue.category === 'number') {
    switch (issue.category) {
      case 0:
        category = ValidationCategory.Connectivity
        break
      case 1:
        category = ValidationCategory.Configuration
        break
      case 2:
        category = ValidationCategory.GraphStructure
        break
      default:
        category = ValidationCategory.Configuration
    }
  } else {
    switch (issue.category) {
      case 'connectivity':
        category = ValidationCategory.Connectivity
        break
      case 'configuration':
        category = ValidationCategory.Configuration
        break
      case 'graph_structure':
        category = ValidationCategory.GraphStructure
        break
      default:
        category = ValidationCategory.Configuration
    }
  }

  return {
    id: issue.id,
    nodeId: issue.nodeId,
    nodeLabel: issue.nodeLabel,
    nodeType: issue.nodeType,
    severity,
    category,
    code: issue.code,
    message: issue.message,
    details: issue.details,
    handleId: issue.handleId,
  }
}

/**
 * Builds the nodeStates map from a list of validation issues
 */
function buildNodeStates(issues: ValidationIssue[]): Map<string, NodeValidationState> {
  const nodeStates = new Map<string, NodeValidationState>()

  for (const issue of issues) {
    if (!issue.nodeId) continue

    const existing = nodeStates.get(issue.nodeId)
    if (existing) {
      existing.issues.push(issue)
      if (issue.severity === ValidationSeverity.Error) {
        existing.hasErrors = true
        existing.errorCount++
      } else {
        existing.hasWarnings = true
        existing.warningCount++
      }
    } else {
      nodeStates.set(issue.nodeId, {
        nodeId: issue.nodeId,
        hasErrors: issue.severity === ValidationSeverity.Error,
        hasWarnings: issue.severity === ValidationSeverity.Warning,
        errorCount: issue.severity === ValidationSeverity.Error ? 1 : 0,
        warningCount: issue.severity === ValidationSeverity.Warning ? 1 : 0,
        issues: [issue],
      })
    }
  }

  return nodeStates
}

/**
 * Maps server validation result DTO to client WorkflowValidationResult
 */
export function mapServerValidationToClient(
  serverResult: ServerValidationResultDto
): WorkflowValidationResult {
  // Map all issues
  const errors = serverResult.errors.map(mapServerIssueToClient)
  const warnings = serverResult.warnings.map(mapServerIssueToClient)
  const allIssues = [...errors, ...warnings]

  // Build node states from all issues
  const nodeStates = buildNodeStates(allIssues)

  // Map graph analysis
  const graphAnalysis: GraphAnalysisResult = {
    hasUnreachableNodes: serverResult.graphAnalysis.hasUnreachableNodes,
    unreachableNodeIds: serverResult.graphAnalysis.unreachableNodeIds,
    hasCycles: serverResult.graphAnalysis.hasCycles,
    cycleNodeIds: serverResult.graphAnalysis.cycleNodeIds,
    hasOrphanedPaths: serverResult.graphAnalysis.hasOrphanedPaths,
    orphanedPathNodeIds: serverResult.graphAnalysis.orphanedPathNodeIds,
  }

  return {
    isValid: serverResult.isValid,
    timestamp: new Date(serverResult.timestamp),
    errorCount: serverResult.errorCount,
    warningCount: serverResult.warningCount,
    errors,
    warnings,
    nodeStates,
    graphAnalysis,
  }
}
